import { useScrollReveal } from "../../../hooks/useScrollReveal";

const steps = [
  {
    num: "01",
    title: "Audit & Identify Gaps",
    desc: "We map your current operations to uncover missed opportunities, inefficiencies, and system gaps.",
    icon: "ri-search-2-line",
  },
  {
    num: "02",
    title: "Structure Your Systems",
    desc: "We design a structured framework tailored to your workflows, pipelines, and team processes.",
    icon: "ri-layout-masonry-line",
  },
  {
    num: "03",
    title: "Deploy AI Automation",
    desc: "Intelligent automation deployed across operations—capturing, routing, and executing without manual effort.",
    icon: "ri-robot-line",
  },
  {
    num: "04",
    title: "Optimize & Scale",
    desc: "We continuously monitor performance, refine systems, and build infrastructure for sustainable growth.",
    icon: "ri-line-chart-line",
  },
];

export default function ProcessSection() {
  const { ref: headRef, visible: headVisible } = useScrollReveal(0.1);

  return (
    <section className="w-full bg-[#07080C] py-36 px-6 overflow-hidden">
      {/* Header */}
      <div
        ref={headRef as React.RefObject<HTMLDivElement>}
        className="max-w-7xl mx-auto text-center mb-28"
        style={{
          opacity: headVisible ? 1 : 0,
          transform: headVisible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}
      >
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4ADE80]/80 mb-5 block">
          Our Process
        </span>
        <h2
          className="font-extrabold text-white leading-[0.95] tracking-[-0.04em]"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(2.8rem, 5vw, 5rem)",
          }}
        >
          How We Build Your Foundation
        </h2>
      </div>

      {/* Full-width process visual */}
      <div className="max-w-7xl mx-auto mb-20 relative rounded-3xl overflow-hidden" style={{ height: "360px" }}>
        <img
          src="https://public.readdy.ai/ai/img_res/2030cf593084a62117077dc5e881a098.jpg"
          alt="Our process"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080C]/90 via-[#07080C]/20 to-[#07080C]/70" />

        {/* Step numbers floating on image */}
        <div className="absolute inset-0 flex items-center justify-around px-10">
          {steps.map((step) => (
            <div key={step.num} className="text-center">
              <span
                className="font-black text-white/20 leading-none block"
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(3rem, 6vw, 6rem)" }}
              >
                {step.num}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Step detail cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-px bg-white/[0.05] rounded-2xl overflow-hidden">
        {steps.map((step, i) => (
          <StepCard key={step.num} step={step} delay={i * 0.1} />
        ))}
      </div>
    </section>
  );
}

function StepCard({ step, delay }: { step: { num: string; title: string; desc: string; icon: string }; delay: number }) {
  const { ref, visible } = useScrollReveal(0.1);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="bg-[#07080C] p-10 group hover:bg-[#0D0E14] transition-colors duration-300"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      <div className="w-10 h-10 flex items-center justify-center mb-6">
        <i className={`${step.icon} text-[#4ADE80] text-xl group-hover:scale-110 transition-transform duration-300`} />
      </div>
      <h3
        className="text-lg font-bold text-white mb-3 tracking-[-0.02em]"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {step.title}
      </h3>
      <p className="text-sm text-white/35 leading-relaxed">{step.desc}</p>
    </div>
  );
}
